import React, { useCallback, useEffect, useRef, useState } from "react";
import { LifeBuoy, X } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";
import {
  HELP_TOPICS,
  HELP_GREETING,
  HELP_SUPPORT,
} from "../../../constants/interviewInstructions";

// "Need help?" launcher pinned to the foot of the step rail, plus the chat
// panel it opens. Purely local: picking a topic appends a scripted reply, so
// nothing here touches the check flow or its state.
const TYPING_DELAY_MS = 550;

const HelpChat = ({ showSecondaryCamera = false }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [isOpen, setIsOpen] = useState(false);
  // Scripted transcript: { key, from: "bot" | "user", text?, topic? }
  const [messages, setMessages] = useState([
    { key: "greeting", from: "bot", text: HELP_GREETING },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [askedIds, setAskedIds] = useState([]);

  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimer = useRef(null);

  const topics = HELP_TOPICS.filter(
    (topic) => topic.id !== "secondaryCamera" || showSecondaryCamera
  );

  const close = useCallback(() => setIsOpen(false), []);

  // Esc closes; a click anywhere outside the panel or its launcher does too.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    const onPointerDown = (event) => {
      if (
        panelRef.current?.contains(event.target) ||
        launcherRef.current?.contains(event.target)
      ) {
        return;
      }
      close();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [isOpen, close]);

  // Follow the transcript as it grows, including while the dots are showing.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
  }, [messages, isTyping, isOpen]);

  // A pending reply must not land after the component goes away.
  useEffect(() => () => clearTimeout(typingTimer.current), []);

  const handlePickTopic = (topic) => {
    clearTimeout(typingTimer.current);
    const stamp = `${topic.id}-${Date.now()}`;

    setMessages((current) => [
      ...current,
      { key: `${stamp}-q`, from: "user", text: topic.label },
    ]);
    setAskedIds((current) =>
      current.includes(topic.id) ? current : [...current, topic.id]
    );
    setIsTyping(true);

    typingTimer.current = setTimeout(() => {
      setIsTyping(false);
      setMessages((current) => [
        ...current,
        { key: `${stamp}-a`, from: "bot", topic },
      ]);
    }, TYPING_DELAY_MS);
  };

  const bubbleBot = isLight
    ? "bg-white border-[#ECEDF5] text-slate-600"
    : "bg-gray-800/70 border-gray-700/60 text-gray-300";
  const mutedText = isLight ? "text-slate-400" : "text-gray-500";

  const renderBotBubble = (message) => {
    if (message.text) {
      return (
        <p className="text-[14px] leading-relaxed whitespace-pre-line">
          {message.text}
        </p>
      );
    }

    const { topic } = message;
    return (
      <>
        <p className="text-[14px] leading-relaxed">{topic.lead}</p>
        <ol className="mt-2.5 space-y-2">
          {topic.steps.map((step, index) => (
            <li key={step} className="flex items-start gap-2.5">
              <span
                className={`w-[18px] h-[18px] mt-px shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  isLight
                    ? "bg-[#F1F2F7] text-slate-500"
                    : "bg-gray-700 text-gray-300"
                }`}
              >
                {index + 1}
              </span>
              <span className="text-[13px] leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>
      </>
    );
  };

  return (
    // The inner wrapper is the positioning context, so the panel is anchored to
    // the button itself rather than to the top of the spacer above it.
    <div className="mt-auto pt-6">
      <div className="relative">
      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Help"
          className={`absolute bottom-full left-0 mb-3 w-[460px] max-w-[calc(100vw-2.5rem)] rounded-2xl border overflow-hidden origin-bottom-left animate-[helpChatIn_140ms_ease-out] ${
            isLight
              ? "bg-[#FAFAFB] border-[#E6E7EC] shadow-[0_18px_44px_rgba(16,24,40,0.16)]"
              : "bg-gray-900 border-gray-700 shadow-[0_18px_44px_rgba(0,0,0,0.55)]"
          }`}
        >
          <style>{`
            @keyframes helpChatIn {
              from { opacity: 0; transform: translateY(8px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes helpChatDot {
              0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
              30%           { opacity: 1;    transform: translateY(-2px); }
            }
            /* Scrolls, but shows no bar. The class selector is what makes this
               stick — the light theme sets a visible bar on a bare \`*\`. */
            .help-chat-scroll {
              scrollbar-width: none;
              -ms-overflow-style: none;
            }
            .help-chat-scroll::-webkit-scrollbar { width: 0; height: 0; display: none; }
          `}</style>

          {/* Header */}
          <div
            className={`flex items-center gap-2.5 px-4 py-3 border-b ${
              isLight
                ? "bg-white border-[#EFEFF3]"
                : "bg-gray-800/60 border-gray-700/60"
            }`}
          >
            <span className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center bg-gradient-to-br from-[#2F64FF] to-[#FB54AE]">
              <LifeBuoy className="w-4 h-4 text-white" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <p
                className={`text-[15px] font-medium leading-tight ${
                  isLight ? "text-slate-900" : "text-white"
                }`}
              >
                Need help?
              </p>
              <p className={`text-[12px] leading-tight mt-px ${mutedText}`}>
                Pick what's giving you trouble
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close help"
              className={`ml-auto p-1.5 rounded-full transition-colors ${
                isLight
                  ? "text-slate-400 hover:text-slate-600 hover:bg-[#F1F2F7]"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-700/60"
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Transcript */}
          <div
            ref={scrollRef}
            className="help-chat-scroll px-4 py-4 space-y-3 max-h-[46vh] min-h-[180px] overflow-y-auto"
          >
            {messages.map((message) =>
              message.from === "user" ? (
                <div key={message.key} className="flex justify-end">
                  <span className="max-w-[80%] rounded-2xl rounded-br-md px-3.5 py-2 text-[14px] font-medium text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE]">
                    {message.text}
                  </span>
                </div>
              ) : (
                <div key={message.key} className="flex">
                  <div
                    className={`max-w-[92%] rounded-2xl rounded-bl-md border px-3.5 py-2.5 ${bubbleBot}`}
                  >
                    {renderBotBubble(message)}
                  </div>
                </div>
              )
            )}

            {isTyping && (
              <div className="flex">
                <div
                  className={`rounded-2xl rounded-bl-md border px-3.5 py-3 flex items-center gap-1 ${bubbleBot}`}
                >
                  {[0, 1, 2].map((dot) => (
                    <span
                      key={dot}
                      className={`w-1.5 h-1.5 rounded-full ${
                        isLight ? "bg-slate-400" : "bg-gray-400"
                      }`}
                      style={{
                        animation: `helpChatDot 1s ${dot * 0.15}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Topic chips */}
          <div
            className={`px-4 pt-3 pb-3.5 border-t ${
              isLight
                ? "bg-white border-[#EFEFF3]"
                : "bg-gray-800/40 border-gray-700/60"
            }`}
          >
            <p
              className={`text-[12px] font-medium tracking-[0.08em] uppercase mb-2 ${mutedText}`}
            >
              {askedIds.length ? "Something else?" : "I need help with"}
            </p>
            <div className="flex flex-wrap gap-2">
              {topics.map((topic) => {
                const Icon = topic.icon;
                const asked = askedIds.includes(topic.id);
                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => handlePickTopic(topic)}
                    disabled={isTyping}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isLight
                        ? asked
                          ? "bg-[#F1F2F7] border-[#E6E7EC] text-slate-500 hover:text-slate-700"
                          : "bg-white border-[#D8DAE2] text-slate-700 hover:border-[#2F64FF] hover:text-[#2F64FF]"
                        : asked
                        ? "bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200"
                        : "bg-gray-800/60 border-gray-600 text-gray-200 hover:border-[#2F64FF] hover:text-white"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {topic.label}
                  </button>
                );
              })}
            </div>

            <p className={`text-[12px] leading-relaxed mt-3 ${mutedText}`}>
              Still stuck? Write to{" "}
              <a
                href={`mailto:${HELP_SUPPORT.email}`}
                className="text-blue-500 font-medium hover:underline"
              >
                {HELP_SUPPORT.email}
              </a>{" "}
              or call{" "}
              <a
                href={HELP_SUPPORT.phoneHref}
                className="text-blue-500 font-medium hover:underline"
              >
                {HELP_SUPPORT.phone}
              </a>
            </p>
          </div>
        </div>
      )}

      <button
        ref={launcherRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-full border text-[14px] font-medium transition-colors ${
          isLight
            ? isOpen
              ? "bg-white border-[#2F64FF] text-[#2F64FF] shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
              : "bg-white border-[#E6E7EC] text-slate-600 hover:border-[#D8DAE2] hover:text-slate-900 shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
            : isOpen
            ? "bg-gray-800 border-[#2F64FF] text-white"
            : "bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600 hover:text-white"
        }`}
      >
        <LifeBuoy className="w-4 h-4 shrink-0" strokeWidth={1.75} />
        Need help?
      </button>
      </div>
    </div>
  );
};

export default HelpChat;
